import { sortBy } from "lodash";
import { getAllData } from "./fetching";
import {
  LIST_DIGITAL_LAB_INSTRUMENT_REPOSITORY_USERS,
  LIST_IR_GROUP_ADMIN_MAPPINGS
} from "../../gql/logBooksapi";
import { REPO_CONFIG } from "../../constants";

type UserType = {
  email: string;
  familyName: string;
  givenName: string;
  name: string;
  userId: string;
};

type IRGroupAdminMapping = {
  groupName: string;
  reviewers?: UserType[];
  userIds: UserType[];
};

type IRGroupAdminMappingResponse = {
  items: IRGroupAdminMapping[];
  nextToken: string | null;
  error: string | null;
};

const hasReviewers = (
  mapping: IRGroupAdminMapping
): mapping is IRGroupAdminMapping & Required<Pick<IRGroupAdminMapping, "reviewers">> => !!mapping.reviewers;

export const extractReviewersIds = (groupsResults: IRGroupAdminMappingResponse[]): string[] =>
  Array.from(
    new Set(
      groupsResults
        .flatMap((groupsResult) => groupsResult.items)
        .filter(hasReviewers)
        .flatMap((i) => i.reviewers)
        .map((reviewer) => reviewer.userId)
    )
  );

export const getReviewers = async (client: any, equipmentGroup: string, currentUser: { user: string }) => {
  const [{ items: userItems }, ...groupsResults] = await Promise.all([
    getAllData({
      client,
      query: LIST_DIGITAL_LAB_INSTRUMENT_REPOSITORY_USERS,
      fetchPolicy: "network-only",
      dataPath: ["data", "listDigitalLabInstrumentRepositoryUsers"],
      drillData: true,
      variables: { limit: 1000 },
      awsAppSyncConfig: REPO_CONFIG as any
    }),
    ...equipmentGroup.split("").map((_character: string, index: number, charactersArray: string[]) => {
      return getAllData({
        client,
        query: LIST_IR_GROUP_ADMIN_MAPPINGS,
        fetchPolicy: "network-only",
        dataPath: ["data", "listIRGroupAdminMappings"],
        drillData: true,
        variables: {
          limit: 1000,
          groupName: {
            eq: charactersArray.slice(0, index + 1).join("")
          }
        },
        awsAppSyncConfig: REPO_CONFIG as any
      });
    })
  ]);

  const groupsReviewersIds = extractReviewersIds(groupsResults as IRGroupAdminMappingResponse[]);

  const list = (userItems as UserType[])
    .filter((item) => item?.userId !== currentUser.user && groupsReviewersIds.includes(item?.userId))
    .map((item) => ({
      ...item,
      key: item?.email,
      value:
        item?.givenName && item?.familyName
          ? item?.name
            ? `${item?.givenName} ${item?.familyName} - ${item?.name}`
            : `${item?.givenName} ${item?.familyName}`
          : item.email
    }));

  return sortBy(list, "value");
};
